import React, { useState, useEffect } from 'react';
import { FORM_DEFINITIONS, industryRoles } from '../../definitions';
import { generateSocialMediaPost } from '../../services/socialMediaService';
import { generateColdEmailSequence } from '../../services/coldEmailService';
import { generateLinkedInDMSequence } from '../../services/linkedinService';
import ResultsModal from '../../components/ResultsModal/ResultsModal';
import LoadingPopup from '../../components/LoadingPopup/LoadingPopup';

interface FormData {
  [key: string]: string;
}

export default function AgentCWriter() {
  const [selectedForm, setSelectedForm] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any>(null);
  const [showResults, setShowResults] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [availableRoles, setAvailableRoles] = useState<Array<{ value: string; label: string }>>([]);
  const [error, setError] = useState<string | null>(null);

  // Update available roles when industry changes
  useEffect(() => {
    if (formData.industry) {
      const roles = industryRoles[formData.industry] || industryRoles.default;
      setAvailableRoles(roles);
      // Clear the selected role if industry changes
      if (formData.role && !roles.find(r => r.value === formData.role)) {
        setFormData(prev => ({ ...prev, role: '' }));
      }
    }
  }, [formData.industry]);

  const handleInputChange = (fieldId: string, value: string) => {
    setFormData(prev => {
      const newData = { ...prev, [fieldId]: value };
      
      // Clear dependent fields when parent field changes
      const currentForm = FORM_DEFINITIONS[selectedForm];
      if (currentForm) {
        currentForm.fields.forEach(field => {
          if (field.dependsOn === fieldId) {
            newData[field.id] = '';
          }
        });
      }
      
      return newData;
    });
  };

  const handleContentTypeSelect = (type: string) => {
    setSelectedForm(type);
      setStep(2);
    setFormData({}); // Clear form when changing content type
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      let response;
      
      if (selectedForm === 'social-media-post') {
        response = await generateSocialMediaPost({
          platform: formData.platform,
          post_objective: formData.postObjective,
          target_audience: formData.targetAudience,
          key_message: formData.keyMessage,
          additional_context: formData.additionalContext
        });
      } else if (selectedForm === 'cold-email') {
        response = await generateColdEmailSequence({
          industry: formData.industry,
          target_role: formData.role,
          tone_of_voice: formData.tone,
          value_proposition: formData.offer === 'custom' ? formData.customOffer : formData.offer,
          additional_context: formData.additionalContext
        });
      } else if (selectedForm === 'linkedin-dm') {
        response = await generateLinkedInDMSequence({
          industry: formData.industry,
          target_role: formData.role,
          tone_of_voice: formData.tone,
          value_proposition: formData.offer === 'custom' ? formData.customOffer : formData.offer,
          additional_context: formData.additionalContext
        });
      }

      if (response) {
        setResults(response);
        setShowResults(true);
      }
    } catch (error) {
      console.error('Error:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const currentForm = FORM_DEFINITIONS[selectedForm];

  const renderStep1 = () => (
    <div className="bg-secondary/5 rounded-xl p-8">
      <h1 className="text-3xl font-bold mb-4">Select Content Type</h1>
      <p className="text-gray-400 mb-8">Choose the type of content you want to generate</p>
      
          <select
        value={selectedForm}
        onChange={(e) => handleContentTypeSelect(e.target.value)}
        className="w-full bg-background border border-secondary/20 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
      >
        <option value="">Select content type</option>
        {Object.entries(FORM_DEFINITIONS).map(([id, form]) => (
          <option key={id} value={id}>
            {form.title}
              </option>
            ))}
          </select>
        </div>
  );

  const renderStep2 = () => (
    <div className="bg-secondary/5 rounded-xl p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">{currentForm.title}</h1>
          <p className="text-gray-400">{currentForm.description}</p>
        </div>
        <button
          onClick={() => {
            setStep(1);
            setSelectedForm('');
            setFormData({});
          }}
          className="text-gray-400 hover:text-white transition-colors"
        >
          ← Back to content selection
        </button>
        </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {currentForm.fields.map(field => {
          // Skip rendering if field has showIf condition and it's not met
          if (field.showIf && !field.showIf(formData)) {
            return null;
          }

          // For role field, use dynamically populated roles
          const options = field.id === 'role' ? availableRoles : field.options;

  return (
            <div key={field.id} className="space-y-2">
              <label htmlFor={field.id} className="block text-sm font-medium text-gray-300">
                {field.label}
              </label>
              {field.type === 'select' ? (
              <select
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="w-full bg-background border border-secondary/20 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  required={field.required}
                >
                  <option value="">{`Select ${field.label}`}</option>
                  {options?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                  </option>
                ))}
              </select>
              ) : (
                <textarea
                  id={field.id}
                  value={formData[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  className="w-full bg-background border border-secondary/20 rounded-lg px-4 py-3 text-text-primary focus:outline-none focus:border-primary"
                  rows={4}
                  placeholder={field.placeholder}
                  required={field.required}
                />
              )}
            </div>
          );
        })}

              <button
                type="submit"
                disabled={isLoading}
          className="w-full bg-primary text-white py-3 px-6 rounded-lg hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
          {isLoading ? 'Generating...' : 'Generate'}
              </button>
          </form>
      </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      {step === 1 ? renderStep1() : renderStep2()}

      <LoadingPopup 
        isOpen={isLoading} 
        contentType={selectedForm as 'social-media-post' | 'cold-email' | 'linkedin-dm'} 
      />

      {showResults && (
        <ResultsModal
          isOpen={showResults}
          onClose={() => setShowResults(false)}
          results={results}
          messageType={selectedForm === 'linkedin-dm' ? 'LinkedIn DM' : undefined}
        />
      )}
    </div>
  );
} 